/**
 * @file 通用错误处理 Hook（组件内使用）
 */
import {useNavigate} from 'react-router-dom';
import {useCallback} from 'react';
import type {ArgsProps} from 'antd/es/notification/interface';
import {messageManager} from './MessageManager';
import {baseHandleError} from './baseHandleError';

export interface IUseRequestErrorHandlerOptions {
    error: unknown;
    customMessage?: string;
    message?: any;
    notification?: any;
    modal?: any;
    /**
     * 通知配置项（支持所有 Ant Design notification 配置）
     */
    messageConfig?: Partial<ArgsProps>;
}

/**
 * 通用错误处理 Hook
 * 用于函数组件中处理错误，自动集成路由跳转能力
 * 默认使用 messageManager.notification 和 messageManager.modal
 */
export function useRequestErrorHandler() {
    const navigate = useNavigate();

    /**
     * 简化版：直接传入 error 和 customMessage
     * 默认使用 notification 展示错误
     */
    const handleError = useCallback(
        (error: unknown, customMessage?: string) =>
            baseHandleError(error, {
                messageApi: messageManager.notification as any,
                modalApi: messageManager.modal,
                navigate,
                customMessage
            }),
        [navigate]
    );

    /**
     * 完整版：支持更多配置项
     * 可以自定义 message/notification 和 modal
     */
    const handleErrorWithOptions = useCallback(
        (options: IUseRequestErrorHandlerOptions) => {
            const {
                error,
                customMessage,
                message: customMessage_,
                notification: customNotification = messageManager.notification,
                modal: customModal = messageManager.modal,
                messageConfig
            } = options;

            // 优先使用自定义的 message，否则使用 notification
            const displayApi = customMessage_ || customNotification;

            return baseHandleError(error, {
                messageApi: displayApi as any,
                modalApi: customModal,
                navigate,
                customMessage,
                messageConfig
            });
        },
        [navigate]
    );

    return {handleError, handleErrorWithOptions};
}
